import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Divider,
  useTheme,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  SelectChangeEvent,
  Chip
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Save,
  Close,
  TrendingUp,
  TrendingDown,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  saveRecord,
  Record,
  SaveRecordPayload,
  fetchRecords
} from '../../slices/incomeExpenses/IncomeExpensesSlice';
import {
  fetchMembers,
  setSearchKey
} from 'slices/NdisSlices/staffManagement/createStaffMembersSlice';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { debounce } from 'lodash';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MdDateRange, MdWarningAmber } from 'react-icons/md';

// Extend dayjs with the isBetween plugin
dayjs.extend(isBetween);

interface FormProps {
  open: boolean;
  onClose: () => void;
  editData: Record | null;
}

// Form data interface for react-hook-form
interface FormData {
  personEmail: string;
  personName: string;
  category: string;
  transactionType: 'income' | 'expense';
  projAmt: string;
  actAmt: string;
  timePeriod: string;
  currentDate: string;
  doc_url?: string; // Add doc_url to form data
}

// Interface for category cache
interface CategoryCache {
  name: string;
  transactionType: 'income' | 'expense';
  timestamp: number;
}

// Yup validation schema
const validationSchema = yup.object().shape({
  transactionType: yup
    .string()
    .oneOf(['income', 'expense'], 'Please select a valid transaction type')
    .required('Transaction type is required'),
  personEmail: yup
    .string()
    .email('Please enter a valid email address')
    .required('Person email is required'),
  personName: yup
    .string()
    .min(2, 'Person name must be at least 2 characters')
    .required('Person name is required'),
  category: yup
    .string()
    .min(2, 'Category must be at least 2 characters')
    .required('Category is required'),
  projAmt: yup
    .string()
    .required('Projected amount is required')
    .test('is-positive', 'Projected amount must be greater than 0', (value) => {
      return parseFloat(value || '0') > 0;
    }),
  actAmt: yup
    .string()
    .required('Actual amount is required')
    .test('is-positive', 'Actual amount must be greater than 0', (value) => {
      return parseFloat(value || '0') > 0;
    }),
  timePeriod: yup.string().required('Time period is required'),
  currentDate: yup.string().required('Transaction date is required')
});

// Fixed time period configurations with proper date ranges
const getTimePeriodsConfig = (year: number) => {
  const selectedYear = dayjs().year(year);
  const currentMonth = dayjs().month();
  const currentMonthInSelectedYear = selectedYear.month(currentMonth);

  return {
    quarter_one: {
      label: 'Quarter One',
      start: selectedYear.month(0).date(1),
      end: selectedYear.month(2).endOf('month'),
      range: `Jan 01 - Mar 31, ${year}`
    },
    quarter_two: {
      label: 'Quarter Two',
      start: selectedYear.month(3).date(1),
      end: selectedYear.month(5).endOf('month'),
      range: `Apr 01 - Jun 30, ${year}`
    },
    quarter_three: {
      label: 'Quarter Three',
      start: selectedYear.month(6).date(1),
      end: selectedYear.month(8).endOf('month'),
      range: `Jul 01 - Sep 30, ${year}`
    },
    quarter_four: {
      label: 'Quarter Four',
      start: selectedYear.month(9).date(1),
      end: selectedYear.month(11).endOf('month'),
      range: `Oct 01 - Dec 31, ${year}`
    },
    current_month: {
      label: 'Current Month',
      start: currentMonthInSelectedYear.startOf('month'),
      end: currentMonthInSelectedYear.endOf('month'),
      range: `${currentMonthInSelectedYear.format('MMM')} 01 - ${currentMonthInSelectedYear
        .endOf('month')
        .format('MMM DD, YYYY')}`
    },
    current_fortnight: {
      label: 'Current Fortnight',
      start: currentMonthInSelectedYear.startOf('month'),
      end: currentMonthInSelectedYear.startOf('month').add(13, 'days'),
      range: `${currentMonthInSelectedYear.format('MMM')} 01 - ${currentMonthInSelectedYear
        .startOf('month')
        .add(13, 'days')
        .format('MMM DD, YYYY')}`
    },
    this_year: {
      label: 'This Year',
      start: selectedYear.startOf('year'),
      end: selectedYear.endOf('year'),
      range: `Jan 01 - Dec 31, ${year}`
    }
  };
};

const timeFilters = [
  'quarter_one',
  'quarter_two',
  'quarter_three',
  'quarter_four',
  'current_month',
  'current_fortnight',
  'this_year'
];

// Helper function to get user details from localStorage
const getUserFromLocalStorage = () => {
  try {
    const userDetailsStr = localStorage.getItem('user-details');
    if (userDetailsStr) {
      const userDetails = JSON.parse(userDetailsStr);
      return {
        name: userDetails.name || '',
        email: userDetails.email || ''
      };
    }
  } catch (error) {
    console.error('Error parsing user details from localStorage:', error);
  }
  return { name: '', email: '' };
};

const IncomeExpensesForm: React.FC<FormProps> = ({ open, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { members, loading } = useSelector((state: RootState) => state.createStaffMembers);
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );
  const currentYear = useAppSelector((state) => state.incomeExpenses.current_year);
  const { filter, time_filter } = useAppSelector((state) => state.incomeExpenses);

  const [inputValue, setInputValue] = useState('');
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [dateError, setDateError] = useState<string>('');
  const [isResetting, setIsResetting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  // Category cache state - persists until modal is closed
  const [categoryCache, setCategoryCache] = useState<CategoryCache[]>([]);

  // Get user details from localStorage
  const currentUser = useMemo(() => getUserFromLocalStorage(), []);

  const memberOptions = useMemo(() => {
    if (!currentUser.email) return members || [];
    const exists = (members || []).some((m: any) => m.email === currentUser.email);
    if (exists) return members || [];
    return [
      {
        name: currentUser.name || currentUser.email,
        email: currentUser.email
      },
      ...(members || [])
    ];
  }, [members, currentUser]);

  const memberFilter = useMemo(
    () =>
      createFilterOptions<any>({
        stringify: (option) => `${option.name || ''} ${option.email || ''}`
      }),
    []
  );

 
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    trigger,
    formState: { errors, isValid, isDirty, isSubmitting }
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      personEmail: '',
      personName: '',
      category: '',
      transactionType: 'income',
      projAmt: '',
      actAmt: '',
      timePeriod: 'current_month',
      currentDate: dayjs().format('YYYY-MM-DD'),
      doc_url: ''
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true
  });

  // Watch form values
  const watchedTimePeriod = watch('timePeriod');
  const watchedTransactionType = watch('transactionType');
  const watchedCategory = watch('category');

  // Predefined categories
  const predefinedCategories = ['Housing', 'Food', 'Transportation', 'Entertainment', 'Other'];

  // Get categories filtered by current transaction type
  const getFilteredCachedCategories = (transactionType: 'income' | 'expense') => {
    return categoryCache
      .filter((cache) => cache.transactionType === transactionType)
      .map((cache) => cache.name)
      .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates
  };

  // Combined categories for autocomplete (predefined + cached for current transaction type)
  const combinedCategories = useMemo(() => {
    const cachedForCurrentType = getFilteredCachedCategories(watchedTransactionType);
    // FIXED: Use Array.from instead of spread on Set
    const allCategories = Array.from(new Set([...predefinedCategories, ...cachedForCurrentType]));
    return allCategories;
  }, [watchedTransactionType, categoryCache]);

  // Check if current category is already used
  const isCategoryAlreadyUsed = useMemo(() => {
    if (!watchedCategory || watchedCategory.trim() === '') return false;
    return getFilteredCachedCategories(watchedTransactionType).includes(watchedCategory.trim());
  }, [watchedCategory, watchedTransactionType, categoryCache]);

  // Get time periods configuration using the selected year from state
  const timePeriodsConfig = useMemo(
    () => getTimePeriodsConfig(parseInt(currentYear)),
    [currentYear]
  );

  // Get current time period configuration
  const currentTimePeriodConfig =
    timePeriodsConfig[watchedTimePeriod as keyof typeof timePeriodsConfig];

  // Debounced search for members
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const payload = {
          group_id: selectedProjectId || '',
          service_name_id: selectedServiceTitle || '',
          search_key: value || ''
        };
        dispatch(setSearchKey(value));
        dispatch(fetchMembers(payload));
      }, 500),
    [dispatch, selectedProjectId, selectedServiceTitle]
  );

  // Function to add category to cache
  const addCategoryToCache = (category: string, transactionType: 'income' | 'expense') => {
    if (!category || category.trim() === '') return;
    const trimmedCategory = category.trim();
    const existsInCache = categoryCache.some(
      (cache) => cache.name === trimmedCategory && cache.transactionType === transactionType
    );
    if (!existsInCache) {
      setCategoryCache((prev) => [
        ...prev,
        {
          name: trimmedCategory,
          transactionType,
          timestamp: Date.now()
        }
      ]);
    }
  };

  // Function to refresh records after save
  const refreshRecords = () => {
    if (selectedProjectId && selectedServiceTitle) {
      dispatch(
        fetchRecords({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          transaction_type: filter.toLowerCase(),
          current_year: currentYear,
          time_filter: time_filter,
          page_size: 10,
          continuation_token: null
        })
      );
    }
  };

  // Fetch members when modal opens and handle cleanup
  useEffect(() => {
    if (open) {
      debouncedSearch('');
      setInputValue('');
      setStep(1);
      setDateError('');
      setIsResetting(false);
    } else {
      // Clear category cache when modal closes
      setCategoryCache([]);
    }
    return () => debouncedSearch.cancel();
  }, [open, debouncedSearch]);

  // Populate form data when editing
  useEffect(() => {
    if (editData && open) {
      reset({
        personEmail: editData.personEmail,
        personName: editData.personName,
        category: editData.category,
        transactionType: editData.transactionType,
        projAmt: editData.projAmt.toString(),
        actAmt: editData.actAmt.toString(),
        timePeriod: 'current_month',
        currentDate: dayjs().format('YYYY-MM-DD')
      });
      setInputValue(editData.personName);
      setSelectedDate(dayjs());
      setStep(2);
    } else if (open && currentUser.email && currentUser.name) {
      // Set default user details when not editing
      const defaultValues = {
        personEmail: currentUser.email,
        personName: currentUser.name,
        category: '',
        transactionType: 'income' as const,
        projAmt: '',
        actAmt: '',
        timePeriod: 'current_month',
        currentDate: dayjs().format('YYYY-MM-DD')
      };
      
      reset(defaultValues);
      setInputValue(currentUser.name);
      setSelectedDate(dayjs());
    }
  }, [editData, open, reset, currentUser]);

  // Update selected date when time period changes
  useEffect(() => {
    if (currentTimePeriodConfig) {
      const defaultDate = currentTimePeriodConfig.start;
      setSelectedDate(defaultDate);
      setValue('currentDate', defaultDate.format('YYYY-MM-DD'));
      setDateError('');
    }
  }, [watchedTimePeriod, currentTimePeriodConfig, setValue]);

  // Validate date is within selected time period range
  const validateDateRange = (date: Dayjs | null): boolean => {
    if (!date || !currentTimePeriodConfig) return false;
    const isWithinRange = date.isBetween(
      currentTimePeriodConfig.start,
      currentTimePeriodConfig.end,
      'day',
      '[]'
    );
    if (!isWithinRange) {
      setDateError(`Date must be between ${currentTimePeriodConfig.range}`);
      setSnackbar({
        open: true,
        message: `Selected date is outside the valid range for ${currentTimePeriodConfig.label}. Please select a date between ${currentTimePeriodConfig.range}`,
        severity: 'warning'
      });
    } else {
      setDateError('');
    }
    return isWithinRange;
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const isValid = validateDateRange(newValue);
      if (isValid) {
        setSelectedDate(newValue);
        setValue('currentDate', newValue.format('YYYY-MM-DD'));
      }
    }
  };

  const handleNextStep = () => {
    if (watchedTransactionType) {
      setStep(2);
    }
  };

  const handlePersonChange = (_event: React.SyntheticEvent, value: any) => {
    if (value) {
      setValue('personEmail', value.email, { shouldValidate: true, shouldDirty: true });
      setValue('personName', value.name, { shouldValidate: true, shouldDirty: true });
      setInputValue(value.name);
      // Clear any existing errors
      setTimeout(() => {
        clearErrors(['personEmail', 'personName']);
      }, 0);
    } else {
      setValue('personEmail', '', { shouldValidate: false });
      setValue('personName', '', { shouldValidate: false });
      setInputValue('');
    }
  };

  const handleInputValueChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    const currentPersonName = watch('personName');
    if (newInputValue !== currentPersonName) {
      debouncedSearch(newInputValue);
    }
  };

  const onSubmit = async (data: FormData) => {
    const matchedMember = (members || []).find(
      (m: any) =>
        m.email === data.personEmail ||
        m.email === currentUser.email ||
        m.name === data.personName ||
        m.name === inputValue
    );

    const resolvedPersonEmail =
      data.personEmail || matchedMember?.email || currentUser.email || '';
    const resolvedPersonName =
      data.personName || matchedMember?.name || currentUser.name || '';
   
    
    // Validate all required fields
    if (!resolvedPersonEmail || !resolvedPersonEmail.trim()) {
      console.error('❌ Validation failed: personEmail missing');
      setSnackbar({
        open: true,
        message: 'Please select a person (email required)',
        severity: 'error'
      });
      return;
    }
    if (!resolvedPersonName || !resolvedPersonName.trim()) {
      console.error('❌ Validation failed: personName missing');
      setSnackbar({
        open: true,
        message: 'Person name is required',
        severity: 'error'
      });
      return;
    }
    if (!data.category || !data.category.trim()) {
      console.error('❌ Validation failed: category missing');
      setSnackbar({
        open: true,
        message: 'Please select a category',
        severity: 'error'
      });
      return;
    }
    if (!data.projAmt || parseFloat(data.projAmt) <= 0) {
      console.error('❌ Validation failed: projAmt invalid');
      setSnackbar({
        open: true,
        message: 'Projected amount must be greater than 0',
        severity: 'error'
      });
      return;
    }
    if (!data.actAmt || parseFloat(data.actAmt) <= 0) {
      console.error('❌ Validation failed: actAmt invalid');
      setSnackbar({
        open: true,
        message: 'Actual amount must be greater than 0',
        severity: 'error'
      });
      return;
    }
    if (!validateDateRange(selectedDate)) {
      console.error('❌ Validation failed: date not in range');
      setSnackbar({
        open: true,
        message:
          'Cannot save transaction with invalid date. Please select a date within the valid range.',
        severity: 'error'
      });
      return;
    }
    const payload: SaveRecordPayload = {
      group_id: selectedProjectId || '',
      service_id: selectedServiceTitle || '',
      current_year: currentYear,
      current_date: data.currentDate,
      record_data: {
        id: editData?.id,
        personEmail: resolvedPersonEmail,
        personName: resolvedPersonName,
        category: data.category,
        transactionType: data.transactionType,
        projAmt: parseFloat(data.projAmt) || 0,
        actAmt: parseFloat(data.actAmt) || 0,
        doc_url: data.doc_url || ''
      }
    };
    
    try {
      await dispatch(saveRecord(payload)).unwrap();
      // Add category to cache after successful save
      addCategoryToCache(data.category, data.transactionType);
      refreshRecords();
      setSnackbar({
        open: true,
        message: editData ? 'Record updated successfully' : 'Record added successfully',
        severity: 'success'
      });
      setIsResetting(true);
      clearErrors();
      const resetData = {
        personEmail: currentUser.email,
        personName: currentUser.name,
        category: '',
        transactionType: data.transactionType,
        projAmt: '',
        actAmt: '',
        timePeriod: data.timePeriod,
        currentDate: data.currentDate
      };
      reset(resetData, {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepDefaultValues: true
      });
      setInputValue(currentUser.name);
      setTimeout(() => {
        clearErrors();
      }, 100);
      setTimeout(() => {
        setIsResetting(false);
      }, 300);
    } catch (error) {
     
      setSnackbar({
        open: true,
        message: 'Failed to save record. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            outline: 'none',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
            {editData ? 'Edit Transaction' : 'Add New Transaction'}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 1 && (
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}
                  >
                    Select Transaction Type *
                  </Typography>
                  <Controller
                    name="transactionType"
                    control={control}
                    render={({ field }) => (
                      <ToggleButtonGroup
                        value={field.value}
                        exclusive
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            field.onChange(newValue);
                          }
                        }}
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            py: 1.5,
                            flex: 1,
                            border: '1px solid transparent',
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            transition: 'all 0.3s ease',
                            '&:first-of-type': {
                              borderTopLeftRadius: '8px',
                              borderBottomLeftRadius: '8px'
                            },
                            '&:last-of-type': {
                              borderTopRightRadius: '8px',
                              borderBottomRightRadius: '8px'
                            },
                            '&:not(:first-of-type)': {
                              marginLeft: 0,
                              borderLeft: '1px solid transparent'
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows[2]
                            }
                          }
                        }}
                      >
                        <ToggleButton
                          value="income"
                          sx={{
                            color:
                              field.value === 'income'
                                ? String(theme.palette.success.dark)
                                : String(theme.palette.text.secondary),
                            backgroundColor:
                              field.value === 'income'
                                ? String(theme.palette.success.light)
                                : String(theme.palette.grey[100]),
                            borderColor:
                              field.value === 'income'
                                ? String(theme.palette.success.main)
                                : errors.transactionType && !isResetting
                                ? String(theme.palette.error.main)
                                : String(theme.palette.grey[300]),
                            '&:hover': {
                              backgroundColor:
                                field.value === 'income'
                                  ? String(theme.palette.success.light)
                                  : String(theme.palette.grey[200]),
                              borderColor: String(theme.palette.success.main)
                            },
                            '&.Mui-selected': {
                              backgroundColor: String(theme.palette.success.light),
                              color: String(theme.palette.success.dark),
                              border: `1px solid ${String(theme.palette.success.main)}`,
                              boxShadow: `0 2px 4px ${String(theme.palette.success.light)}`
                            }
                          }}
                        >
                          <TrendingUp
                            sx={{
                              mr: 1,
                              fontSize: '1.2rem',
                              color:
                                field.value === 'income'
                                  ? String(theme.palette.success.dark)
                                  : 'inherit'
                            }}
                          />
                          <Typography variant="body2">Income</Typography>
                        </ToggleButton>
                        <ToggleButton
                          value="expense"
                          sx={{
                            color:
                              field.value === 'expense'
                                ? String(theme.palette.error.dark)
                                : String(theme.palette.text.secondary),
                            backgroundColor:
                              field.value === 'expense'
                                ? String(theme.palette.error.light)
                                : String(theme.palette.grey[100]),
                            borderColor:
                              field.value === 'expense'
                                ? String(theme.palette.error.main)
                                : errors.transactionType && !isResetting
                                ? String(theme.palette.error.main)
                                : String(theme.palette.grey[300]),
                            '&:hover': {
                              backgroundColor:
                                field.value === 'expense'
                                  ? String(theme.palette.error.light)
                                  : String(theme.palette.grey[200]),
                              borderColor: String(theme.palette.error.main)
                            },
                            '&.Mui-selected': {
                              backgroundColor: String(theme.palette.error.light),
                              color: String(theme.palette.error.dark),
                              border: `1px solid ${String(theme.palette.error.main)}`,
                              boxShadow: `0 2px 4px ${String(theme.palette.error.light)}`
                            }
                          }}
                        >
                          <TrendingDown
                            sx={{
                              mr: 1,
                              fontSize: '1.2rem',
                              color:
                                field.value === 'expense'
                                  ? String(theme.palette.error.dark)
                                  : 'inherit'
                            }}
                          />
                          <Typography variant="body2">Expense</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                  {errors.transactionType && !isResetting && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.transactionType.message}
                    </Typography>
                  )}
                </Box>

                {/* Display previously used categories for selected transaction type */}
                {getFilteredCachedCategories(watchedTransactionType).length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}
                    >
                      Previously Used Categories ({watchedTransactionType})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getFilteredCachedCategories(watchedTransactionType).map(
                        (category, index) => (
                          <Chip
                            key={`${category}-${index}`}
                            label={category}
                            size="small"
                            variant="outlined"
                            icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
                            sx={{
                              borderColor:
                                watchedTransactionType === 'income'
                                  ? String(theme.palette.success.main)
                                  : String(theme.palette.error.main),
                              color:
                                watchedTransactionType === 'income'
                                  ? String(theme.palette.success.dark)
                                  : String(theme.palette.error.dark),
                              backgroundColor:
                                watchedTransactionType === 'income'
                                  ? String(theme.palette.success.light)
                                  : String(theme.palette.error.light),
                              '& .MuiChip-icon': {
                                color:
                                  watchedTransactionType === 'income'
                                    ? String(theme.palette.success.main)
                                    : String(theme.palette.error.main)
                              }
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>
                )}

                {/* Step 1 Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Close sx={{ fontSize: '1rem' }} />}
                    onClick={onClose}
                    fullWidth
                    sx={{
                      borderColor: String(theme.palette.grey[400]),
                      color: String(theme.palette.text.primary),
                      '& .MuiButton-startIcon': {
                        marginRight: '4px'
                      }
                    }}
                  >
                    <Typography variant="body2">Cancel</Typography>
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ArrowForward sx={{ fontSize: '1rem' }} />}
                    onClick={handleNextStep}
                    fullWidth
                    disabled={!watchedTransactionType || (!!errors.transactionType && !isResetting)}
                    sx={{
                      backgroundColor: String(theme.palette.primary.main),
                      '&:hover': { backgroundColor: String(theme.palette.primary.dark) },
                      '& .MuiButton-startIcon': {
                        marginRight: '4px'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Next
                    </Typography>
                  </Button>
                </Stack>
              </Stack>
            )}

            {step === 2 && (
              <Stack spacing={3}>
                {/* Transaction Type Display */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}
                  >
                    Transaction Type *
                  </Typography>
                  <Controller
                    name="transactionType"
                    control={control}
                    render={({ field }) => (
                      <ToggleButtonGroup
                        value={field.value}
                        exclusive
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            field.onChange(newValue);
                          }
                        }}
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            py: 1.5,
                            flex: 1,
                            border: '1px solid transparent',
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            transition: 'all 0.3s ease',
                            '&:first-of-type': {
                              borderTopLeftRadius: '8px',
                              borderBottomLeftRadius: '8px'
                            },
                            '&:last-of-type': {
                              borderTopRightRadius: '8px',
                              borderBottomRightRadius: '8px'
                            },
                            '&:not(:first-of-type)': {
                              marginLeft: 0,
                              borderLeft: '1px solid transparent'
                            },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows[2]
                            }
                          }
                        }}
                      >
                        <ToggleButton
                          value="income"
                          sx={{
                            color:
                              field.value === 'income'
                                ? String(theme.palette.success.dark)
                                : String(theme.palette.text.secondary),
                            backgroundColor:
                              field.value === 'income'
                                ? String(theme.palette.success.light)
                                : String(theme.palette.grey[100]),
                            borderColor:
                              field.value === 'income'
                                ? String(theme.palette.success.main)
                                : String(theme.palette.grey[300]),
                            '&:hover': {
                              backgroundColor:
                                field.value === 'income'
                                  ? String(theme.palette.success.light)
                                  : String(theme.palette.grey[200]),
                              borderColor: String(theme.palette.success.main)
                            },
                            '&.Mui-selected': {
                              backgroundColor: String(theme.palette.success.light),
                              color: String(theme.palette.success.dark),
                              border: `1px solid ${String(theme.palette.success.main)}`,
                              boxShadow: `0 2px 4px ${String(theme.palette.success.light)}`
                            }
                          }}
                        >
                          <TrendingUp
                            sx={{
                              mr: 1,
                              fontSize: '1.2rem',
                              color:
                                field.value === 'income'
                                  ? String(theme.palette.success.dark)
                                  : 'inherit'
                            }}
                          />
                          <Typography variant="body2">Income</Typography>
                        </ToggleButton>
                        <ToggleButton
                          value="expense"
                          sx={{
                            color:
                              field.value === 'expense'
                                ? String(theme.palette.error.dark)
                                : String(theme.palette.text.secondary),
                            backgroundColor:
                              field.value === 'expense'
                                ? String(theme.palette.error.light)
                                : String(theme.palette.grey[100]),
                            borderColor:
                              field.value === 'expense'
                                ? String(theme.palette.error.main)
                                : String(theme.palette.grey[300]),
                            '&:hover': {
                              backgroundColor:
                                field.value === 'expense'
                                  ? String(theme.palette.error.light)
                                  : String(theme.palette.grey[200]),
                              borderColor: String(theme.palette.error.main)
                            },
                            '&.Mui-selected': {
                              backgroundColor: String(theme.palette.error.light),
                              color: String(theme.palette.error.dark),
                              border: `1px solid ${String(theme.palette.error.main)}`,
                              boxShadow: `0 2px 4px ${String(theme.palette.error.light)}`
                            }
                          }}
                        >
                          <TrendingDown
                            sx={{
                              mr: 1,
                              fontSize: '1.2rem',
                              color:
                                field.value === 'expense'
                                  ? String(theme.palette.error.dark)
                                  : 'inherit'
                            }}
                          />
                          <Typography variant="body2">Expense</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Box>

                {/* Time Period Section */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
                  >
                    Time Period & Date Selection
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Time Period Select */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="timePeriod"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.timePeriod && !isResetting}
                          >
                            <InputLabel>Time Period *</InputLabel>
                            <Select
                              value={field.value}
                              label="Time Period *"
                              onChange={(event: SelectChangeEvent<string>) => {
                                field.onChange(event.target.value);
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                            >
                              {timeFilters.map((filter) => (
                                <MenuItem key={filter} value={filter}>
                                  {timePeriodsConfig[filter as keyof typeof timePeriodsConfig]
                                    ?.label ||
                                    filter
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.timePeriod && !isResetting && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {errors.timePeriod.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* Date Picker */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="currentDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Transaction Date *"
                            value={selectedDate}
                            onChange={handleDateChange}
                            minDate={currentTimePeriodConfig?.start}
                            maxDate={currentTimePeriodConfig?.end}
                            slotProps={{
                              textField: {
                                size: 'small',
                                variant: 'outlined',
                                fullWidth: true,
                                error: (!!dateError || !!errors.currentDate) && !isResetting,
                                helperText: !isResetting
                                  ? dateError || errors.currentDate?.message
                                  : undefined
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {/* Time Range Display */}
                  {currentTimePeriodConfig && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1.5,
                        backgroundColor: dateError
                          ? String(theme.palette.error.light)
                          : String(theme.palette.primary.light),
                        borderRadius: 2,
                        border: `1px solid ${
                          dateError
                            ? String(theme.palette.error.main)
                            : String(theme.palette.primary.main)
                        }`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: '50%',
                          backgroundColor: dateError
                            ? String(theme.palette.error.main)
                            : String(theme.palette.primary.main),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: 24,
                          flexShrink: 0
                        }}
                      >
                        {dateError ? <MdWarningAmber /> : <MdDateRange />}
                      </Box>

                      {/* Text Section */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: dateError
                              ? String(theme.palette.error.dark)
                              : String(theme.palette.primary.dark),
                            fontWeight: 500,
                            display: 'block'
                          }}
                        >
                          {dateError ? 'Invalid Date Range' : 'Valid Date Range'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: dateError
                              ? String(theme.palette.error.dark)
                              : String(theme.palette.primary.dark),
                            fontWeight: 600,
                            mt: 0.3
                          }}
                        >
                          {currentTimePeriodConfig.range}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Person Selection */}
                <Controller
                  name="personEmail"
                  control={control}
                  render={({ field }) => {
                    const selectedMember = memberOptions.find((m: any) => m.email === field.value);
                    return (
                      <Autocomplete
                        fullWidth
                        options={memberOptions}
                        getOptionLabel={(option: any) => option.name || ''}
                        isOptionEqualToValue={(option: any, value: any) =>
                          option.email === value.email
                        }
                        value={selectedMember || null}
                        onChange={handlePersonChange}
                        inputValue={inputValue}
                        onInputChange={handleInputValueChange}
                        loading={loading}
                        openOnFocus
                        onOpen={() => debouncedSearch('')}
                        noOptionsText="No employees found"
                        clearOnBlur={false}
                        selectOnFocus={true}
                        handleHomeEndKeys={true}
                        filterOptions={(options, state) => {
                          if (!state.inputValue) return options;
                          if (selectedMember && state.inputValue === selectedMember.name) return options;
                          return memberFilter(options, state);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Person *"
                            size="small"
                            variant="outlined"
                            error={false}
                          helperText={undefined}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <li {...props} key={option.email}>
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  );
                }}
                />

                {/* Category Selection */}
                <Box>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        options={combinedCategories}
                        value={field.value}
                        onChange={(_, newValue) => {
                          field.onChange(newValue || '');
                          if (newValue && newValue.length >= 2) {
                            setTimeout(() => clearErrors('category'), 0);
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          field.onChange(newInputValue);
                          if (newInputValue && newInputValue.length >= 2) {
                            setTimeout(() => clearErrors('category'), 0);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category *"
                            size="small"
                            variant="outlined"
                            error={false}
                            helperText={undefined}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isCategoryAlreadyUsed && (
                                    <CheckCircle
                                      sx={{
                                        color:
                                          watchedTransactionType === 'income'
                                            ? String(theme.palette.success.main)
                                            : String(theme.palette.error.main),
                                        mr: 1
                                      }}
                                    />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option: string) => {
                          const isUsed =
                            getFilteredCachedCategories(watchedTransactionType).includes(option);
                          return (
                            <li {...props} key={option}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {option}
                                </Typography>
                                {isUsed && (
                                  <CheckCircle
                                    sx={{
                                      color:
                                        watchedTransactionType === 'income'
                                          ? String(theme.palette.success.main)
                                          : String(theme.palette.error.main),
                                      fontSize: '16px',
                                      ml: 1
                                    }}
                                  />
                                )}
                              </Box>
                            </li>
                          );
                        }}
                      />
                    )}
                  />
                  {isCategoryAlreadyUsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        color:
                          watchedTransactionType === 'income'
                            ? String(theme.palette.success.dark)
                            : String(theme.palette.error.dark),
                        fontWeight: 500
                      }}
                    >
                      ✓ This category was already used in this session
                    </Typography>
                  )}
                </Box>

                {/* Projected Amount */}
                <Controller
                  name="projAmt"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Projected Amount *"
                      type="number"
                      size="small"
                      variant="outlined"
                      error={!!errors.projAmt && !isResetting}
                      helperText={!isResetting ? errors.projAmt?.message : undefined}
                      InputProps={{
                        startAdornment: (
                          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                            $
                          </Typography>
                        )
                      }}
                    />
                  )}
                />

                {/* Actual Amount */}
                <Controller
                  name="actAmt"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Actual Amount *"
                      type="number"
                      size="small"
                      variant="outlined"
                      error={!!errors.actAmt && !isResetting}
                      helperText={!isResetting ? errors.actAmt?.message : undefined}
                      InputProps={{
                        startAdornment: (
                          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                            $
                          </Typography>
                        )
                      }}
                    />
                  )}
                />

                {/* Step 2 Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Close sx={{ fontSize: '1rem' }} />}
                    onClick={onClose}
                    fullWidth
                    sx={{
                      borderColor: String(theme.palette.grey[400]),
                      color: String(theme.palette.text.primary),
                      '& .MuiButton-startIcon': {
                        marginRight: '4px'
                      }
                    }}
                  >
                    <Typography variant="body2">Close</Typography>
                  </Button>

                  {/* Save Button - Always visible for all users */}
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save sx={{ fontSize: '1rem' }} />}
                    fullWidth
                    disabled={!!dateError || isSubmitting}
                  
                    sx={{
                      backgroundColor: String(theme.palette.primary.main),
                      '&:hover': { backgroundColor: String(theme.palette.primary.dark) },
                      '&.Mas-disabled': {
                        backgroundColor: String(theme.palette.grey[300]),
                        color: String(theme.palette.grey)
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '4px'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {editData ? 'Update' : 'Save'}
                      </Typography>
                    )}
                  </Button>
                </Stack>

              </Stack>
            )}
          </form>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={snackbar.severity === 'success' ? 4000 : 6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{
                width: '100%',
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                  fontWeight: 500
                }
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
};

export default IncomeExpensesForm;
